import numbro from 'numbro';

import BudgetModes from '@/common/enums/budget-modes.enum';
import CPMCalculator from '@/common/models/CPMCalculator';
import Plan from '@/common/models/plan/Plan';
import PlanBreakdownData from '@/common/models/plan/PlanBreakdownData';
import { IPlanAPI } from '@/common/models/plan/types/PlanAPI';
import { DASH } from '@/globals';
import MapCart from '@/org-app/components/inventory/overallMap/MapCart';
import { useMediaOwners } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import rootState from '@/store/state';
import formatNumber from '@/utils/NumberFormatter';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

import {
  BreakdownParameterEnum,
  isLocation,
  isMediaType,
  TargetingItem,
  TBreakdownTypeAPI,
  TTypeOfData,
} from './BudgetBreakdownTypes';

class BudgetBreakdownService extends Validatable {
  name: string;

  eCPM: number;

  type: TTypeOfData;

  screenCount: number;

  siteCount: number;

  networkCount: number;

  budget: number;

  impressions: number;

  impressionCapacity: number;

  plan: Plan;

  breakdownParameter: TBreakdownTypeAPI;

  inventoryTargeting: TargetingItem;

  budgetInput: string;

  percentBudgetInput: string;

  isSharedPlan: boolean;

  sharedPlanEstimatedBudget: number;

  sharedPlanEstimatedImpressions: number;

  isSupplySharedPlan: boolean;

  isStroeerSharedPlan: boolean;

  audienceConcentration: number;

  targetingName: string;

  constructor(
    type: TTypeOfData = 'data-budget-breakdown',
    plan: Plan = null,
    breakdownParameter: TBreakdownTypeAPI = null,
    inventoryTargeting: TargetingItem = null,
    name = null,
    id = -1,
    budget = 0,
    impressions = 0,
    eCPM = null,
    screenCount = 0,
    siteCount = 0,
    networkCount = 0,
    impressionCapacity = 0,
    audienceConcentration = 0,
    targetingName = null,
  ) {
    super();
    this.type = type;
    this.name = name;
    this.id = id;
    this.eCPM = eCPM;
    this.screenCount = screenCount;
    this.siteCount = siteCount;
    this.networkCount = networkCount;
    this.impressionCapacity = impressionCapacity;
    this.budget = budget;
    this.impressions = impressions;
    this.plan = plan;
    this.breakdownParameter = breakdownParameter;
    this.inventoryTargeting = inventoryTargeting;
    this.audienceConcentration = audienceConcentration;
    this.targetingName = targetingName;

    // Needed for data binding
    this.budgetInput = null;
    this.percentBudgetInput = null;

    // Shared plan values
    this.isSharedPlan = false;
    this.sharedPlanEstimatedBudget = null;
    this.sharedPlanEstimatedImpressions = null;
    this.isSupplySharedPlan = false;
    this.isStroeerSharedPlan = false;
  }

  get eCPMFormatted() {
    if (this.type === 'data-totals') {
      return null;
    }
    return this.eCPM != null ? i18n.global.n(this.eCPM, 'currency') : DASH;
  }

  get screenCountFormatted() {
    return this.type === 'data-totals' ? null : formatNumber(this.screenCount, '0,0');
  }

  get siteCountFormatted() {
    return this.type === 'data-totals' ? null : formatNumber(this.siteCount, '0,0');
  }

  get networkCountFormatted() {
    return this.type === 'data-totals' ? null : formatNumber(this.networkCount, '0,0');
  }

  get impressionCapacityFormatted() {
    return this.type === 'data-totals' ? null : formatNumber(this.impressionCapacity, '0,0');
  }

  get audienceConcentrationFormatted() {
    return this.type === 'data-totals' ? null : formatNumber(this.audienceConcentration, '0.00%');
  }

  set estimatedBudget(val) {
    if (this.isSharedPlan) {
      this.sharedPlanEstimatedBudget = val;
    }
  }

  get estimatedBudget() {
    if (this.isSharedPlan) {
      return this.sharedPlanEstimatedBudget;
    }
    return this.plan?.cpmCalculator?.estimatedBudget;
  }

  get estimatedBudgetFormatted() {
    if (this.isSharedPlan) {
      const budget = this.plan.budgetMode === BudgetModes.BUDGET ? this.budget : this.estimatedBudget;
      return budget != null ? i18n.global.n(budget, 'currency') : DASH;
    }
    return this.plan?.cpmCalculator?.budgetFormatted;
  }

  get estimatedBudgetFormattedWithoutCurrency() {
    if (this.isSharedPlan) {
      const budget = this.plan.budgetMode === BudgetModes.BUDGET ? this.budget : this.estimatedBudget;
      return budget != null ? i18n.global.n(budget, 'currency') : DASH;
    }
    return this.plan?.cpmCalculator?.budgetFormattedNoCurrency;
  }

  set estimatedImpressions(val) {
    if (this.isSharedPlan) {
      this.sharedPlanEstimatedImpressions = val;
    }
  }

  get estimatedImpressions() {
    if (this.isSharedPlan) {
      return this.sharedPlanEstimatedImpressions;
    }
    return this.plan?.cpmCalculator?.estimatedTargetImpressions;
  }

  get estimatedImpressionsFormatted() {
    if (this.isSharedPlan) {
      const impressions =
        this.plan.budgetMode === BudgetModes.IMPRESSIONS ? this.impressions : this.estimatedImpressions;
      return impressions != null ? formatNumber(impressions, '0,0') : DASH;
    }
    return this.plan?.cpmCalculator?.estimatedTargetImpressionsFormatted;
  }

  get formattedBreakdownSharedName() {
    if (this.targetingName) {
      return this.targetingName;
    }
    const name = this.name.split(' - ');
    return name.length === 2 ? name[0] : this.name;
  }

  get isNew() {
    return this.plan.id === -1;
  }

  static isStroeerSupply() {
    return (
      rootState.orgApp?.organization?.isStroeerOrganization && rootState.orgApp?.organization?.isSupplyOrganization
    );
  }

  static getOwnerName(ownerID): string {
    const mediaOwners = peekQueryInScope(useMediaOwners);
    const match = mediaOwners.find((x) => x.id === ownerID);
    if (match) return match.name;
    return '–';
  }

  static getTargetingName(breakdownParameter, targeting, mapCart?) {
    switch (breakdownParameter) {
      case BreakdownParameterEnum.City:
        return mapCart
          ? mapCart.includedCities.find((city) => targeting.locationIDs.find((id) => city.id === id))
              ?.hierarchyNoCountry
          : targeting.hierarchyNoCountry;
      case BreakdownParameterEnum.VenueType:
        const mediaType = mapCart
          ? mapCart.includedMediaTypes.find((mediaType) => mediaType.id === targeting.mediaTypeIDs[0])
          : targeting;
        return mediaType.formattedName || mediaType.name;
      case BreakdownParameterEnum.Market:
        return mapCart
          ? mapCart.includedMarkets.find((market) => market.id === targeting.marketIDs[0])?.name
          : targeting.name;
      case BreakdownParameterEnum.Network:
        return mapCart
          ? mapCart.includedNetworks.find((network) => network.id === targeting.networkIDs[0])?.name
          : targeting.name;
      case BreakdownParameterEnum.MediaOwner:
        return mapCart ? BudgetBreakdownService.getOwnerName(targeting.mediaOwnerIDs[0]) : targeting.name;
      default:
        return '–';
    }
  }

  getAudienceConcentrationValue(audienceConcentration) {
    if (audienceConcentration) {
      const keys = Object.keys(audienceConcentration);
      if (keys.length > 0 && typeof audienceConcentration[keys[0]] === 'number') {
        return audienceConcentration[keys[0]];
      }
    }
    return 0;
  }

  async getData(isReadOnly: boolean) {
    const params = PlanBreakdownData.toApi(this.plan);
    if (!isReadOnly) {
      params.plan_id = null;
    }
    const response = await rootStore.dispatch('orgApp/getPlanBreakdownData', {
      data: params,
    });

    if (response) {
      this.screenCount = response.unitPhysicalCount;
      this.siteCount = response.siteCount;
      this.impressionCapacity = response.impressions;
      if (BudgetBreakdownService.isStroeerSupply()) {
        this.networkCount = response.impressionsByNetworkID?.length || 0;
      }

      const min = rootState.orgApp?.organization?.isSupplyOrganization ? 'owner_min_cpm' : 'buyer_min_cpm';
      const max = rootState.orgApp?.organization?.isSupplyOrganization ? 'owner_max_cpm' : 'buyer_max_cpm';
      this.plan.cpmCalculator.calculateCPMFromRateCard({
        average: response.ecpm,
        [min]: response.minEcpm,
        [max]: response.maxEcpm,
      });
      this.eCPM = this.plan.cpmCalculator.estimatedAverageCPM || 0;
      if (this.plan.hasMarkup && this.plan.isOverrideMarkup) {
        this.eCPM = this.plan.planFeeValue;
      }
      this.audienceConcentration = this.getAudienceConcentrationValue(response.audienceConcentration);
    }
  }

  public static getTotal(property: keyof BudgetBreakdownService, entities: BudgetBreakdownService[]) {
    return entities.reduce((acc, x) => {
      const formattedNumber = numbro(x[property]).value();
      const number = Number.isNaN(formattedNumber) ? 0 : formattedNumber;
      return acc + number;
    }, 0);
  }

  public static calculateTotalValues(entities: BudgetBreakdownService[]) {
    entities = entities.filter((x) => x.type !== 'data-totals');
    const totalValues = new BudgetBreakdownService('data-totals');

    totalValues.budget = BudgetBreakdownService.getTotal('budget', entities);
    totalValues.impressions = BudgetBreakdownService.getTotal('impressions', entities);
    return totalValues;
  }

  private static getRegionsAndCountriesLocationsIDs(
    locationIDs: number[],
    selectedInventoryTargeting: TargetingItem[],
  ) {
    // Keep country and regional targeting if available
    if (locationIDs?.length > 0) {
      return locationIDs.filter((id) => !selectedInventoryTargeting.some((targeting) => targeting.id === id));
    }
    return [];
  }

  private static async getBreakdownBudgetsWithTotalValues(
    breakdownBudgets: BudgetBreakdownService[],
    isReadOnly: boolean = false,
  ) {
    // Get the dynamic data (screens, sites, eCPm)
    await Promise.all(breakdownBudgets.map((breakdown) => breakdown.getData(isReadOnly)));

    if (!isReadOnly) {
      // Insert the total row values
      breakdownBudgets.push(BudgetBreakdownService.calculateTotalValues(breakdownBudgets));
    }

    return breakdownBudgets;
  }

  private static convertToPlan(
    plan: Plan,
    targeting: TargetingItem,
    selectedInventoryTargeting: TargetingItem[],
    breakdownParameter: TBreakdownTypeAPI,
  ) {
    const childPlan = Plan.fromObject({ ...plan });
    let { name } = targeting;

    // Assign the targeting ids to the breakdown targeting selected
    switch (breakdownParameter) {
      case BreakdownParameterEnum.City:
        childPlan.locationIDs = [
          ...BudgetBreakdownService.getRegionsAndCountriesLocationsIDs(plan.locationIDs, selectedInventoryTargeting),
          targeting.id,
        ];
        if (isLocation(targeting)) {
          name = targeting.hierarchyNoCountry || name;
        }
        break;
      case BreakdownParameterEnum.VenueType:
        childPlan.mediaTypeIDs = [targeting.id];
        if (isMediaType(targeting)) {
          name = targeting.formattedName || name;
        }
        break;
      case BreakdownParameterEnum.Market:
        childPlan.marketIDs = [targeting.id];
        break;
      case BreakdownParameterEnum.Network:
        childPlan.networkIDs = [targeting.id];
        break;
      case BreakdownParameterEnum.MediaOwner:
        childPlan.mediaOwnerIDs = [targeting.id];
        break;
      default:
        break;
    }

    // Reset the child properties
    childPlan.id = -1;
    childPlan.uuid = undefined;
    childPlan.CPM = plan.isCPMFromRateCard ? null : plan.CPM;
    childPlan.cpmNet = null;
    childPlan.planFee = plan.planFee;
    childPlan.name = name;
    childPlan.budget = 0;
    childPlan.targetImpressions = 0;
    childPlan.sharedEntity = null;
    childPlan.isCPMFromRateCard = plan.isCPMFromRateCard;
    childPlan.parentPlanID = plan.id;
    childPlan.modifiedOnUtc = null;
    childPlan.purchaseType = plan.purchaseType;
    childPlan.cpmCalculator = new CPMCalculator(null, null, childPlan);

    return childPlan;
  }

  public static async createBreakdownBudgets(
    breakdownParameter: TBreakdownTypeAPI,
    selectedInventoryTargeting,
    plan: Plan,
  ) {
    let breakdownBudgets: BudgetBreakdownService[] = [];

    if (selectedInventoryTargeting != null) {
      breakdownBudgets = selectedInventoryTargeting.map((targeting: TargetingItem) => {
        const child = BudgetBreakdownService.convertToPlan(
          plan,
          targeting,
          selectedInventoryTargeting,
          breakdownParameter,
        );

        return BudgetBreakdownService.fromObject({
          plan: child,
          id: targeting.id,
          name: child.name,
          inventoryTargeting: targeting,
          breakdownParameter: breakdownParameter,
          targetingName: BudgetBreakdownService.getTargetingName(breakdownParameter, targeting),
        });
      });
      return BudgetBreakdownService.getBreakdownBudgetsWithTotalValues(breakdownBudgets);
    }
    return breakdownBudgets;
  }

  public static async updateExistingBreakdownBudgets(
    isReadOnly: boolean,
    plan: Plan,
    childrenPlan: Array<Plan['toApi']> | Plan[],
    breakdownParameter: TBreakdownTypeAPI,
    mapCart: MapCart,
  ) {
    const breakdownBudgets = childrenPlan.map((x) => {
      const TTDTargetLists = plan.ttdCampaign?.planTTDTargetLists;

      const child = isReadOnly
        ? Plan.fromApi(x)
        : BudgetBreakdownService.updateChildPlanWithParentTargeting(x, plan, mapCart);

      // CAM-6505: Display target list names if breakdown has been pushed to TTD
      if (TTDTargetLists?.length > 0) {
        const matchingTargetList = TTDTargetLists.find((targetList) => targetList.plan_id == child.id);
        child.name = matchingTargetList?.ttd_bidlist_name || child.name;
      }

      child.cpmCalculator = new CPMCalculator(null, null, child);

      return BudgetBreakdownService.fromObject({
        plan: child,
        id: child.id,
        name: child.name,
        budget: child.budget || 0,
        breakdownParameter: breakdownParameter,
        impressions: child.targetImpressions || 0,
        targetingName: BudgetBreakdownService.getTargetingName(breakdownParameter, child, mapCart),
      });
    });
    return BudgetBreakdownService.getBreakdownBudgetsWithTotalValues(breakdownBudgets, isReadOnly);
  }

  static updateChildPlanWithParentTargeting(settings: IPlanAPI, plan: Plan, mapCart?: MapCart): Plan {
    let child = Plan.fromObject({ ...plan });

    child.id = settings.plan_id;
    child.uuid = settings.uuid;
    child.CPM = settings.cpm;
    child.cpmNet = settings.cpm_net;
    child.planFee = plan.planFee;
    child.name = settings.name;
    child.budget = settings.budget;
    child.budgetMode = settings.budget_mode;
    child.targetImpressions = settings.targeted_impressions;
    child.sharedEntity = settings.shared_entity;
    child.isCPMFromRateCard = settings.is_cpm_from_rate_card;
    child.parentPlanID = plan.id;
    child.goal = plan.goal;
    child.purchaseType = plan.purchaseType;
    if (mapCart) {
      child = BudgetBreakdownService.setBreakdownIDs(child, settings, mapCart);
    }
    return child;
  }

  public static setBreakdownIDs(child, settings, mapCart?) {
    switch (child.breakdownType) {
      case BreakdownParameterEnum.City:
        const regions = mapCart.includedRegions.map((x) => x.id);
        const countries = mapCart.includedCountries.map((x) => x.id);
        child.locationIDs = [...regions, ...countries, ...settings.inventory_targeting.location_ids];
        break;
      case BreakdownParameterEnum.VenueType:
        child.mediaTypeIDs = settings.inventory_targeting.mediatype_ids;
        break;
      case BreakdownParameterEnum.Market:
        child.marketIDs = settings.inventory_targeting.market_ids;
        break;
      case BreakdownParameterEnum.Network:
        child.networkIDs = settings.inventory_targeting.network_ids;
        break;
      case BreakdownParameterEnum.MediaOwner:
        child.mediaOwnerIDs = settings.inventory_targeting.owner_ids;
        break;
      default:
        break;
    }
    return child;
  }

  static fromObject(obj) {
    return new BudgetBreakdownService(
      obj.type,
      obj.plan,
      obj.breakdownParameter,
      obj.inventoryTargeting,
      obj.name,
      obj.id,
      obj.budget,
      obj.impressions,
      obj.eCPM,
      obj.screenCount,
      obj.siteCount,
      obj.networkCount,
      obj.impressionCapacity,
      obj.audienceConcentration,
      obj.targetingName,
    );
  }

  //
  // Inline-Editing
  //
  toApi() {
    return this.plan;
  }

  static fromApi(apiObject: IPlanAPI): Plan {
    return Plan.fromObject(apiObject);
  }
}

export default BudgetBreakdownService;
